/**
 * Content types that always show the CES feedback module: insurance pages and
 * insurance claim ("anmälan") pages.
 */
const ELIGIBLE_PAGE_TYPES = new Set(["insurancepage", "insuranceclaimpage"]);

const FEEDBACK_EXCLUDED_PATHS = new Set([
  "/forsakring/sjukforsakring/anmal-sjukskrivning",
  "/forsakring/arbetsskadeforsakring/anmala-arbetsskada",
  "/forsakring/foraldrapenningtillagg/ansok-foraldrapenningtillagg",
]);

/**
 * The Contentful block that renders the "ta reda på vilka försäkringar som
 * gäller för dig" insurance finder. Pages containing this block are the finder
 * pages (the försäkrings- and anmälanväljare) and are feedback-eligible.
 */
const INSURANCE_PICKER_BLOCK_KEY = "insurancepickerblock";

type ContentCollection = { items?: Array<Record<string, unknown> | null> | null } | null;

type WithContentCollections = {
  mainAreaCollection?: ContentCollection;
  mainAreaBottomCollection?: ContentCollection;
};

const normalizePath = (path?: string): string | undefined => {
  if (!path) return undefined;
  let normalizedPath = path;
  while (normalizedPath.endsWith("/")) {
    normalizedPath = normalizedPath.slice(0, -1);
  }
  return normalizedPath || "/";
};

/** Whether the feedback module is explicitly disabled for this URL. */
export const isFeedbackExcludedPath = (path?: string): boolean =>
  FEEDBACK_EXCLUDED_PATHS.has(normalizePath(path) ?? "");

const collectionItems = (data?: WithContentCollections) => [
  ...(data?.mainAreaCollection?.items ?? []),
  ...(data?.mainAreaBottomCollection?.items ?? []),
];

/**
 * Whether the page content contains the insurance finder (picker) block. Each
 * content area item is keyed by its block type (e.g. `{ insurancePickerBlock }`),
 * so we look for that key on any item.
 */
export const pageHasInsurancePicker = (data?: WithContentCollections): boolean =>
  collectionItems(data).some(
    (item) =>
      !!item && Object.keys(item).some((key) => key.toLowerCase() === INSURANCE_PICKER_BLOCK_KEY),
  );

/**
 * Decides whether the CES feedback module should be rendered for a page.
 *
 * Eligible:
 * - all insurance pages and insurance claim pages (by content type)
 * - the insurance finder pages (by containing the insurance picker block)
 *
 * Eligibility is derived from content, not URL routes, so it stays correct
 * regardless of where a page lives in the URL structure.
 */
export const isFeedbackEligible = (pageType?: string, data?: WithContentCollections): boolean => {
  if (pageType && ELIGIBLE_PAGE_TYPES.has(pageType.toLowerCase())) {
    return true;
  }
  return pageHasInsurancePicker(data);
};

/**
 * Derives the 1-based section position for the feedback module, matching the
 * convention used by other tracking contracts (section position = number of
 * preceding content sections + 1). The module is always appended last.
 */
export const getFeedbackSectionIndex = (data?: WithContentCollections): number => {
  const mainCount = data?.mainAreaCollection?.items?.length ?? 0;
  const bottomCount = data?.mainAreaBottomCollection?.items?.length ?? 0;
  return mainCount + bottomCount + 1;
};
